/**
 * The new design's menu (state/nav.ts in the design), pointed at this app's
 * real routes. Labels, order, groups and dividers are the design's.
 *
 * `path: null` — the design has this page but the app does not yet. It shows
 * in the menu exactly as designed and goes nowhere until it is built.
 *
 * Item options: `divider` draws a hairline above the item (from the design);
 * `soon` shows the design's "Soon" pill (unused, as in the design).
 */

export const nav = [
  { id: "ask", label: "Ask Olli", icon: "ask", path: "/ask-olli" },
  { id: "dashboard", label: "Dashboard", icon: "dashboard", path: "/dashboard" },
  { id: "wallet", label: "Wallet", icon: "wallet", path: "/wallet" },
  {
    id: "crm", label: "CRM", icon: "crm", items: [
      { label: "Leads", path: "/crm/leads" },
      { label: "Lead Reports", path: "/crm/leads/reports" },
      { label: "Contacts", path: "/crm/contacts" },
      { label: "Tasks", path: "/crm/tasks" },
      { label: "Scoring Rules", path: "/crm/settings/scoring-rules", divider: true },
      { label: "Assignment Rules", path: "/crm/settings/assignment-rules" },
      { label: "Tags", path: "/crm/settings/tags" },
    ],
  },
  { id: "patients", label: "Patients", icon: "patients", path: "/patients" },
  { id: "appointments", label: "Appointments", icon: "appointments", path: "/appointments" },
  { id: "schedules", label: "Schedules", icon: "schedules", path: "/schedules" },
  {
    id: "treatment-plans", label: "Treatment Plans", icon: "treatment", items: [
      { label: "All Plans", path: "/treatment-plans" },
      { label: "Treatment Types", path: "/settings/treatments", divider: true },
      { label: "Status Definitions", path: "/settings/treatment-statuses" },
      { label: "Instruction Templates", path: "/settings/instruction-templates" },
    ],
  },
  {
    id: "whatsapp", label: "WhatsApp", icon: "whatsapp", items: [
      { label: "Overview", path: "/waba" },
      { label: "Conversations", path: "/waba/conversations" },
      { label: "Templates", path: "/waba/templates" },
      { label: "Campaigns", path: "/waba/campaigns" },
      { label: "API Documentation", path: "/waba/api-docs" },
      { label: "Webhook Forwarder", path: "/waba/settings", divider: true },
    ],
  },
  {
    id: "ai-agents", label: "AI Agents", icon: "ai", items: [
      { label: "Templates", path: "/settings/ai-agent/templates" },
      { label: "Agents", path: "/settings/ai-agent" },
      { label: "Knowledge Base", path: "/knowledge-base" },
    ],
  },
  {
    id: "support", label: "Support", icon: "support", items: [
      { label: "Dashboard", path: "/support/dashboard" },
      { label: "Complaints", path: "/support/complaints" },
      { label: "Feedback", path: "/support/feedback" },
    ],
  },
  {
    id: "marketing", label: "Marketing", icon: "marketing", items: [
      { label: "Dashboard", path: "/marketing/dashboard" },
      { label: "Page Performance", path: "/analytics/page-performance" },
      { label: "Ad Campaigns", path: "/ads/campaigns" },
      { label: "Lead Forms", path: "/ads/lead-forms" },
      { label: "Audiences", path: "/ads/audiences" },
      { label: "Optimization Rules", path: "/ads/rules" },
      { label: "Compose Post", path: "/content/compose", divider: true },
      { label: "Comments", path: "/content/comments" },
      { label: "Social Inbox", path: "/social-inbox" },
      { label: "Automations", path: "/automations" },
      { label: "Instagram", path: "/instagram" },
      { label: "Competitor Research", path: "/research/competitors" },
    ],
  },
  {
    id: "settings", label: "Settings", icon: "settings", items: [
      { label: "Branding", path: "/settings/branding" },
      { label: "Facilities", path: "/settings/facilities" },
      { label: "Departments", path: "/settings/departments" },
      { label: "Staff Members", path: "/settings/staff" },
      { label: "Specialties", path: "/settings/specialties" },
      { label: "Lead Statuses", path: "/settings/lead-statuses" },
      { label: "Integrations", path: "/settings/integrations" },
      { label: "API Keys", path: "/settings/integrations/api-keys" },
      { label: "Sync API Docs", path: "/settings/integrations/api-docs" },
    ],
  },
];

const allPaths = nav.flatMap((g) => (g.items ? g.items.map((i) => i.path) : [g.path])).filter(Boolean);

/**
 * True when `pathname` is this menu path's page or one of its sub-pages —
 * but not when it belongs to a longer menu path (so "Integrations" is not
 * lit up on "API Keys", nor "Agents" on "AI Agent Templates").
 */
export function isIn(pathname, path) {
  if (!path) return false;
  if (pathname !== path && !pathname.startsWith(`${path}/`)) return false;
  return !allPaths.some((p) => p !== path && p.length > path.length && (pathname === p || pathname.startsWith(`${p}/`)));
}

/** Every page the menu links to, with its group for the breadcrumb (design: `pages`). */
export const pages = nav.flatMap((g) =>
  g.items
    ? g.items.map((i) => ({ path: i.path, title: i.label, group: g, soon: !!i.soon }))
    : [{ path: g.path, title: g.label, group: undefined, soon: false }],
);
